import type { Lot, Settings, State } from './types';
import type { StoredExtensionAuth } from '../../types/api';
import { STORAGE_KEYS } from './storageKeys';
import {
  storeAuthInMemory,
  loadAuthFromMemory,
  clearAuthFromMemory,
  migrateAuthFromSessionToMemory,
  cleanupStaleSessionStorage,
} from './secureAuthStorage';
import { createLogger } from '../../shared/logger.js';

const log = createLogger('portal:state-storage');

export function loadLots(): Lot[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.LOTS);
    if (raw) {
      const data = JSON.parse(raw);
      return Array.isArray(data) ? data : [];
    }
  } catch (err) {
    log.warn('Failed to load lots:', err);
  }
  return [];
}

export function saveLots(lots: Lot[]): void {
  try {
    localStorage.setItem(STORAGE_KEYS.LOTS, JSON.stringify(lots));
  } catch (err) {
    log.warn('Failed to save lots:', err);
  }
}

export function loadSettings(fallback: Settings): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.SETTINGS);
    if (raw) {
      const data = JSON.parse(raw);
      return data && typeof data === 'object' ? data : fallback;
    }
  } catch (err) {
    log.warn('Failed to load settings:', err);
  }
  return fallback;
}

export function saveSettings(settings: Settings): void {
  try {
    localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
  } catch (err) {
    log.error('Failed to save settings:', err);
  }
}

export function loadStoredAuth(): StoredExtensionAuth | null {
  // First, try to migrate auth from sessionStorage (recovery from auth flow)
  const migratedAuth = migrateAuthFromSessionToMemory();
  if (migratedAuth) {
    return migratedAuth;
  }

  // Then, check in-memory cache (current session)
  return loadAuthFromMemory();
}

export function saveAuth(authData: StoredExtensionAuth): void {
  try {
    storeAuthInMemory(authData);
  } catch (err) {
    log.error('Failed to save auth:', err);
  }
}

export function clearAuth(): void {
  try {
    clearAuthFromMemory();
  } catch (err) {
    log.error('Failed to clear auth:', err);
  }
}

export function loadStoredViewMode(viewModes: { SUPERADMIN: string; USER: string }): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.VIEW_MODE);
    if (raw === viewModes.SUPERADMIN || raw === viewModes.USER) {
      return raw;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function persistViewModePreference(state: State): void {
  try {
    if (state.user && state.user.isSuperUser) {
      localStorage.setItem(STORAGE_KEYS.VIEW_MODE, state.viewMode as string);
    } else {
      localStorage.removeItem(STORAGE_KEYS.VIEW_MODE);
    }
  } catch {
    /* ignore */
  }
}
